import { useMemo } from "react";
import type { Asset } from "../types/deal";
import type { MapCoordinate } from "../components/common/maps/GoogleMap";

/**
 * Hook to convert asset data to map coordinates
 */
export const useAssetMap = (assets: Asset | Asset[]) => {
  const mapCoordinates = useMemo((): MapCoordinate[] => {
    const assetArray = Array.isArray(assets) ? assets : [assets];

    return assetArray
      .filter((asset) => asset.latitude && asset.longitude) // Only include assets with coordinates
      .map((asset) => {
        // Get sector name - handle both array and object cases
        const sectorName =
          Array.isArray(asset.sector) && asset.sector.length > 0
            ? asset.sector[0].sector
            : (asset.sector as any)?.sector;

        // Get deal name and id
        const dealName = (asset.deal as any)?.deal_name;
        const dealId = (asset.deal as any)?.id;

        return {
          lat: asset.latitude,
          lng: asset.longitude,
          title: asset.asset_name,
          description: `${asset.address_1}${asset.address_2 ? ", " + asset.address_2 : ""}, ${asset.city}${asset.postcode ? ", " + asset.postcode : ""}`,
          id: asset.id,
          sector: sectorName,
          dealName: dealName,
          dealId: dealId,
          acquisitionPrice: asset.acquisition_price,
          valuationAmount: asset.valuation_amount,
          totalArea: asset.total_area,
          areaType: asset.area_type,
          currency: asset.currency,
          category: "Asset",
        };
      });
  }, [assets]);

  const hasValidCoordinates = mapCoordinates.length > 0;

  return {
    coordinates: mapCoordinates,
    hasValidCoordinates,
    coordinateCount: mapCoordinates.length,
  };
};

/**
 * Hook to get map center and zoom for asset locations
 */
export const useAssetMapSettings = (coordinates: MapCoordinate[]) => {
  const mapSettings = useMemo(() => {
    if (coordinates.length === 0) {
      return { center: { lat: 0, lng: 0 }, zoom: 2 };
    }

    if (coordinates.length === 1) {
      return {
        center: { lat: coordinates[0].lat, lng: coordinates[0].lng },
        zoom: 15,
      };
    }

    // Calculate center for multiple coordinates
    const avgLat =
      coordinates.reduce((sum, coord) => sum + coord.lat, 0) /
      coordinates.length;
    const avgLng =
      coordinates.reduce((sum, coord) => sum + coord.lng, 0) /
      coordinates.length;

    // Calculate appropriate zoom based on coordinate spread
    const latSpread =
      Math.max(...coordinates.map((c) => c.lat)) -
      Math.min(...coordinates.map((c) => c.lat));
    const lngSpread =
      Math.max(...coordinates.map((c) => c.lng)) -
      Math.min(...coordinates.map((c) => c.lng));
    const maxSpread = Math.max(latSpread, lngSpread);

    let zoom = 10;
    if (maxSpread < 0.1) zoom = 12;
    else if (maxSpread < 1) zoom = 8;
    else if (maxSpread < 5) zoom = 6;
    else if (maxSpread < 20) zoom = 4;
    else zoom = 2;

    return {
      center: { lat: avgLat, lng: avgLng },
      zoom,
    };
  }, [coordinates]);

  return mapSettings;
};
